/* Typed fetch wrapper for the AQ vertical API.
 *
 * Single place that:
 *   - Reads the JWT from auth.ts
 *   - Sets `Authorization: Bearer ...`
 *   - On 401: attempts ONE refresh, then replays the request; on second
 *     401 clears the session and throws so RouteGuard sends to /login.
 *   - Throws typed ApiError on any non-2xx, surfacing server `{ error }`
 *     when available.
 *
 * Per ADR-028:
 *   - Production base URL: https://api.siliconproof.io/v1/v/aq
 *   - Dev: relative `/api` is proxied by Vite to the same place
 *   - All responses must be JSON; binary endpoints not supported here. */

import { readAccessToken, refreshAccessToken, useAuthStore } from './auth';

const ENV_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '');

function apiBaseUrl(): string {
  if (import.meta.env.DEV) return '/api';
  if (!ENV_BASE) return 'https://api.siliconproof.io/v1/v/aq';
  return `${ENV_BASE}/v1/v/aq`;
}

export class ApiError extends Error {
  status: number;
  code: string | undefined;
  details: unknown;
  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the auth header (e.g. for public health checks). */
  noAuth?: boolean;
  /** Internal — used by retry-on-401 to prevent infinite recursion. */
  _isRetry?: boolean;
}

async function doFetch<T>(path: string, opts: ApiFetchOptions): Promise<T> {
  const headers = new Headers(opts.headers as HeadersInit | undefined);
  headers.set('Accept', 'application/json');
  if (opts.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!opts.noAuth) {
    const token = readAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const url = path.startsWith('http') ? path : `${apiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  let res: Response;
  try {
    res = await fetch(url, {
      ...opts,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'network error';
    throw new ApiError(msg, 0, 'NETWORK_ERROR');
  }

  // 401 → try one refresh + replay
  if (res.status === 401 && !opts._isRetry && !opts.noAuth) {
    const tokens = await refreshAccessToken();
    if (tokens) {
      // Write the new tokens into the store/session so the replay sees them
      const u = useAuthStore.getState().user;
      if (u) useAuthStore.getState().setSession(u, tokens);
      return doFetch<T>(path, { ...opts, _isRetry: true });
    }
    // Refresh failed → wipe session and let caller handle
    useAuthStore.getState().clearSession();
    throw new ApiError('session expired', 401, 'SESSION_EXPIRED');
  }

  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let code: string | undefined;
    let details: unknown;
    try {
      const j = (await res.json()) as { error?: string; message?: string; code?: string; details?: unknown };
      msg = j.error || j.message || msg;
      code = j.code;
      details = j.details;
    } catch {
      /* body wasn't JSON — keep status text */
    }
    throw new ApiError(msg, res.status, code, details);
  }

  // 204 No Content — return undefined as T
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  return doFetch<T>(path, opts);
}

/** Convenience wrappers. */
export const api = {
  get: <T>(path: string, opts: Omit<ApiFetchOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts: Omit<ApiFetchOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts: Omit<ApiFetchOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts: Omit<ApiFetchOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts: Omit<ApiFetchOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
